import React, { useMemo } from 'react';

export interface DataPoint {
  label: string;
  value: number;
  color?: string;
}

interface LineChartProps {
  data: DataPoint[];
  title?: string;
  height?: number;
  showGrid?: boolean;
  showLabels?: boolean;
  color?: string;
  fillColor?: string;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  title,
  height = 300,
  showGrid = true,
  showLabels = true,
  color = '#3b82f6',
  fillColor = 'rgba(59, 130, 246, 0.1)'
}) => {
  const chartData = useMemo(() => {
    if (data.length === 0) return null;

    const values = data.map(d => d.value);
    const max = Math.max(...values);
    const min = Math.min(...values, 0);
    const range = max - min || 1;

    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartWidth = 800;
    const chartHeight = height - padding.top - padding.bottom;
    const chartWidthInner = chartWidth - padding.left - padding.right;

    const points = data.map((d, i) => ({
      x: padding.left + (i * chartWidthInner) / Math.max(data.length - 1, 1),
      y: padding.top + chartHeight - ((d.value - min) / range) * chartHeight,
      value: d.value,
      label: d.label
    }));

    // Generate path for line
    const linePath = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');

    // Generate path for area fill
    const areaPath = [
      `M ${points[0].x} ${padding.top + chartHeight}`,
      ...points.map(p => `L ${p.x} ${p.y}`),
      `L ${points[points.length - 1].x} ${padding.top + chartHeight}`,
      'Z'
    ].join(' ');

    // Y-axis labels
    const yTicks = 5;
    const yLabels = Array.from({ length: yTicks }, (_, i) => {
      const value = min + (range * i) / (yTicks - 1);
      const y = padding.top + chartHeight - ((value - min) / range) * chartHeight;
      return { value: Math.round(value), y };
    });

    return {
      points,
      linePath,
      areaPath,
      yLabels,
      chartWidth,
      padding,
      chartHeight
    };
  }, [data, height]);

  if (!chartData || data.length === 0) {
    return (
      <div className="flex items-center justify-center bg-gray-50 rounded-lg" style={{ height }}>
        <p className="text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {title && <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>}
      <svg
        viewBox={`0 0 ${chartData.chartWidth} ${height}`}
        className="w-full"
        style={{ height: `${height}px` }}
      >
        {/* Grid lines */}
        {showGrid && chartData.yLabels.map((label, i) => (
          <line
            key={i}
            x1={chartData.padding.left}
            y1={label.y}
            x2={chartData.chartWidth - chartData.padding.right}
            y2={label.y}
            stroke="#e5e7eb"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
        ))}

        {/* Area fill */}
        <path
          d={chartData.areaPath}
          fill={fillColor}
        />

        {/* Line */}
        <path
          d={chartData.linePath}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {chartData.points.map((point, i) => (
          <g key={i}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill="white"
              stroke={color}
              strokeWidth="2"
            />
            <title>{`${point.label}: ${point.value}`}</title>
          </g>
        ))}

        {/* Y-axis labels */}
        {chartData.yLabels.map((label, i) => (
          <text
            key={i}
            x={chartData.padding.left - 10}
            y={label.y}
            textAnchor="end"
            alignmentBaseline="middle"
            className="text-xs fill-gray-600"
          >
            {label.value}
          </text>
        ))}

        {/* X-axis labels */}
        {showLabels && chartData.points.map((point, i) => (
          <text
            key={i}
            x={point.x}
            y={height - 10}
            textAnchor="middle"
            className="text-xs fill-gray-600"
          >
            {point.label}
          </text>
        ))}

        {/* Axes */}
        <line
          x1={chartData.padding.left}
          y1={chartData.padding.top}
          x2={chartData.padding.left}
          y2={chartData.padding.top + chartData.chartHeight}
          stroke="#9ca3af"
          strokeWidth="1"
        />
        <line
          x1={chartData.padding.left}
          y1={chartData.padding.top + chartData.chartHeight}
          x2={chartData.chartWidth - chartData.padding.right}
          y2={chartData.padding.top + chartData.chartHeight}
          stroke="#9ca3af"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
};

export default LineChart;
